import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { NAV } from "@/lib/nav";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Caixa from "@/pages/Caixa";
import Movimentacoes from "@/pages/Movimentacoes";
import Alunos from "@/pages/Alunos";
import Produtos from "@/pages/Produtos";
import Planos from "@/pages/Planos";
import Pagamentos from "@/pages/Pagamentos";
import Relatorios from "@/pages/Relatorios";
import Usuarios from "@/pages/Usuarios";
import Vendedores from "@/pages/Vendedores";
import Auditoria from "@/pages/Auditoria";

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} richColors closeButton position="top-right" />;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen grid place-items-center bg-background" data-testid="loading-screen">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
    </div>
  );
}

function Protected() {
  const { user } = useAuth();
  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout />;
}

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function PermRoute({ page, children }) {
  const { user } = useAuth();
  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  if (user.role === "admin") return children;
  if (isLoading) return <LoadingScreen />;
  const perms = settings?.permissions?.[user.role];
  if (perms && !perms.includes(page)) {
    const first = NAV.find((n) => !n.adminOnly && perms.includes(n.key));
    return <Navigate to={first ? first.to : "/login"} replace />;
  }
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ThemedToaster />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<Protected />}>
              <Route path="/" element={<PermRoute page="dashboard"><Dashboard /></PermRoute>} />
              <Route path="/caixa" element={<PermRoute page="caixa"><Caixa /></PermRoute>} />
              <Route path="/movimentacoes" element={<PermRoute page="movimentacoes"><Movimentacoes /></PermRoute>} />
              <Route path="/alunos" element={<PermRoute page="alunos"><Alunos /></PermRoute>} />
              <Route path="/produtos" element={<PermRoute page="produtos"><Produtos /></PermRoute>} />
              <Route path="/planos" element={<PermRoute page="planos"><Planos /></PermRoute>} />
              <Route path="/pagamentos" element={<PermRoute page="pagamentos"><Pagamentos /></PermRoute>} />
              <Route path="/relatorios" element={<PermRoute page="relatorios"><Relatorios /></PermRoute>} />
              <Route
                path="/vendedores"
                element={
                  <RoleRoute roles={["admin"]}>
                    <Vendedores />
                  </RoleRoute>
                }
              />
              <Route
                path="/usuarios"
                element={
                  <RoleRoute roles={["admin"]}>
                    <Usuarios />
                  </RoleRoute>
                }
              />
              <Route
                path="/auditoria"
                element={
                  <RoleRoute roles={["admin"]}>
                    <Auditoria />
                  </RoleRoute>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Outlet />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
